import { Box, Button, Grid2, Paper, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Formik } from "formik";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";
import * as Yup from "yup";
import { createActivity } from "../../api/activity/createActivity";
import { useTranslationHook } from "../../hooks";
import { useFormStyles } from "../../hooks/useFormStyles";
import { setLoading } from "../../store/UI";
import { CreateActivityRequest } from "../../types/model/activity";

const ActivityAdd = () => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountId, projectId } = useParams<{accountId: string, projectId: string}>();
  const formStyles = useFormStyles();

  const session = JSON.parse(localStorage.getItem("session") || "{}");
  const ownerId = session?.user?.id ?? session?.userId;


  const handleSubmit = async (values: {
    name: string;
    description: string;
    startDate: string;
    endDate: string;
  }) => {
    dispatch(setLoading(true));

    const request: CreateActivityRequest = {
      name: values.name,
      description: values.description,
      ...(values.startDate ? { startDate: moment(values.startDate).toDate() } : {}),
      ...(values.endDate ? { endDate: moment(values.endDate).toDate() } : {}),
      managerId: ownerId,
      accountId: Number(accountId),
      ownerId,
    };

    try {
      const response = await createActivity(request, Number(projectId));
      enqueueSnackbar(t("activities.created"), { variant: "success" });
      navigate(
        `/accounts/${accountId}/projects/${projectId}/activities/${response.id}`
      );
    } catch (error: any) {
      const label = error?.response?.data?.label;
      enqueueSnackbar(
        t(label ? `back-end.${label}` : "back-end.unknown"),
        { variant: "error" }
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Typography sx={{ mb: 2 }} variant="h4">
          {t("activities.add-activity")}
        </Typography>

        <Formik
          initialValues={{
            name: "",
            description: "",
            startDate: "",
            endDate: "",
          }}
          onSubmit={handleSubmit}
          validationSchema={Yup.object().shape({
            name: Yup.string().required(t("activities.name-required")),
            description: Yup.string().required(t("activities.description-required")),
          })}
        >
          {({ values, touched, errors, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
            <Box component="form" onSubmit={handleSubmit} sx={{ p: 1 }}>
              <Typography variant="h5" sx={{ mb: 2, mt: 1 }}>
                {t("activities.activity-information")}
              </Typography>

              <Grid2 container spacing={3}>
                <Grid2 size={12}>
                  <TextField
                    fullWidth
                    required
                    id="name"
                    label={t("activities.name")}
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(errors.name && touched.name)}
                    helperText={errors.name || ""}
                    sx={{ ...formStyles }}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 700, mb: 2 }}>
                    {t("projects.dates")}
                  </Typography>

                  <Grid2 container spacing={2}>
                    <Grid2 size={6}>
                      <DatePicker
                        label={t("activities.start-date")}
                        value={values.startDate ? moment(values.startDate) : null}
                        onChange={(date) =>
                          setFieldValue("startDate", date ? date.toISOString() : "")
                        }
                        slotProps={{
                          field: { clearable: true },
                          textField: {
                            fullWidth: true,
                            error: !!(errors.startDate && touched.startDate),
                            helperText: touched.startDate && errors.startDate ? errors.startDate : "",
                            sx: { ...formStyles },
                          },
                        }}
                        format="DD/MM/YYYY"
                        views={["day", "month", "year"]}
                      />
                    </Grid2>

                    <Grid2 size={6}>
                      <DatePicker
                        label={t("activities.end-date")}
                        value={values.endDate ? moment(values.endDate) : null}
                        onChange={(date) =>
                          setFieldValue("endDate", date ? date.toISOString() : "")
                        }
                        slotProps={{
                          field: { clearable: true },
                          textField: {
                            fullWidth: true,
                            error: !!(errors.endDate && touched.endDate),
                            helperText: touched.endDate && errors.endDate ? errors.endDate : "",
                            sx: { ...formStyles },
                          },
                        }}
                        format="DD/MM/YYYY"
                        views={["day", "month", "year"]}
                      />
                    </Grid2>
                  </Grid2>
                </Grid2>

                <Grid2 size={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={4}
                    id="description"
                    label={t("activities.description")}
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(errors.description && touched.description)}
                    helperText={errors.description || ""}
                    sx={{ ...formStyles }}
                  />
                </Grid2>
              </Grid2>

              {/* Ações */}
              <Grid2
                size={12}
                sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
              >
                <Button type="submit" variant="contained">
                  {t("admin.accounts.account-save")}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate(-1)}
                >
                  {t("admin.accounts.account-cancel")}
                </Button>
              </Grid2>
            </Box>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};

export default ActivityAdd;